import { useState, useMemo } from "react";
import { Box, Paper, Stack, Group, Loader, Text, Button } from "@mantine/core";
import { IconCheck, IconInfoCircle, IconPhone } from "@tabler/icons-react";
import type { ScribeStatus } from "./Encounter.scribePrefill";
import type { CallCompositionOption } from "@benchmark/core/types";
import { CallCompositionSelect } from "./CallCompositionSelect";
import { TranscriptViewer } from "./TranscriptViewer";
import { formatCallDate, formatCallDuration } from "@/utils/fhir/communication";
import styles from "./Encounter.scribePrefillStatus.module.css";

/**
 * Props for the scribe prefill status UI component.
 * @agentic connections
 */
export type ScribePrefillStatusProps = {
  scribeLoading: boolean;
  scribeApplied: boolean;
  scribeCompositionId: string | undefined;
  scribeStatus: ScribeStatus;
  manualOverride: boolean;
  isIntakeEncounter: boolean;
  onManualOverride: () => void;
  onRetryPrefill: () => void;
  /** Available compositions for selection */
  availableCompositions?: CallCompositionOption[];
  /** Currently selected composition ID */
  selectedCompositionId?: string | null;
  /** Callback when user selects a composition */
  onCompositionSelect?: (compositionId: string) => void;
};

/**
 * Derived UI state for scribe prefill status.
 * @agentic connections
 */
type DerivedState = "hidden" | "generating" | "selecting" | "applied" | "error";

/**
 * Unified scribe prefill status component.
 * @agentic connections
 */
export const ScribePrefillStatusUI = ({
  scribeLoading,
  scribeApplied,
  scribeCompositionId,
  scribeStatus,
  manualOverride,
  isIntakeEncounter,
  onManualOverride,
  onRetryPrefill,
  availableCompositions = [],
  selectedCompositionId,
  onCompositionSelect,
}: ScribePrefillStatusProps) => {
  const [isChangingCall, setIsChangingCall] = useState(false);
  const isPolling =
    scribeStatus === "loading" || scribeStatus === "found-polling";

  // Derive the current UI state from props
  const derivedState = useMemo((): DerivedState => {
    if (manualOverride || !isIntakeEncounter) return "hidden";
    if (isChangingCall && availableCompositions.length > 0) return "selecting";
    if (scribeApplied && scribeCompositionId) return "applied";
    if (scribeLoading || isPolling) return "generating";
    if (availableCompositions.length > 0) return "selecting";
    if (
      !scribeLoading &&
      availableCompositions.length === 0 &&
      (scribeStatus === "error" || scribeStatus === "found-complete")
    ) {
      return "error";
    }
    return "hidden";
  }, [
    manualOverride,
    isIntakeEncounter,
    isChangingCall,
    availableCompositions.length,
    scribeApplied,
    scribeCompositionId,
    scribeLoading,
    isPolling,
    scribeStatus,
  ]);

  // Find the applied composition's metadata for display
  const appliedComposition = useMemo(() => {
    const targetId = selectedCompositionId ?? scribeCompositionId;
    if (!targetId) return null;
    return availableCompositions.find((c) => c.compositionId === targetId);
  }, [selectedCompositionId, scribeCompositionId, availableCompositions]);

  const handleCompositionSelect = (compositionId: string) => {
    onCompositionSelect?.(compositionId);
    setIsChangingCall(false);
  };

  const handleChangeCall = () => {
    setIsChangingCall(true);
  };

  const handleManualOverride = () => {
    setIsChangingCall(false);
    onManualOverride();
  };

  // Don't render anything for hidden state
  if (derivedState === "hidden") {
    return null;
  }

  return (
    <Box mb="sm">
      <Paper p="sm" radius="md" withBorder shadow="xs">
        <Stack gap="sm">
          {/* Header Zone */}
          <HeaderZone state={derivedState} isPolling={isPolling} />

          {/* Content Zone */}
          <ContentZone
            state={derivedState}
            availableCompositions={availableCompositions}
            selectedCompositionId={
              selectedCompositionId ?? scribeCompositionId ?? null
            }
            appliedComposition={appliedComposition}
            isPolling={isPolling}
            onCompositionSelect={handleCompositionSelect}
          />

          {/* Action Zone */}
          <ActionZone
            state={derivedState}
            onManualOverride={handleManualOverride}
            onRetryPrefill={onRetryPrefill}
            onChangeCall={handleChangeCall}
          />
        </Stack>
      </Paper>
    </Box>
  );
};

/**
 * Props for the header zone sub-component.
 * @agentic connections
 */
type HeaderZoneProps = {
  state: DerivedState;
  isPolling: boolean;
};

/**
 * Header zone for scribe prefill status.
 * @agentic connections
 */
const HeaderZone = ({ state, isPolling }: HeaderZoneProps) => {
  switch (state) {
    case "generating":
      return (
        <Group gap="sm" align="center">
          <Loader size="sm" color="purple" />
          <Text fw={600}>Generating documentation…</Text>
        </Group>
      );

    case "selecting":
      return (
        <Group gap="sm" align="center" justify="space-between">
          <Text fw={600}>Select call to document</Text>
          {isPolling && (
            <Group gap={4} align="center">
              <Loader size={12} color="gray" />
              <Text size="xs" c="dimmed">
                Checking for more…
              </Text>
            </Group>
          )}
        </Group>
      );

    case "applied":
      return (
        <Group gap="sm" align="center">
          <IconCheck size={18} color="var(--brand-fill)" />
          <Text fw={600}>Documentation prefilled from call</Text>
        </Group>
      );

    case "error":
      return (
        <Group gap="sm" align="center">
          <IconInfoCircle size={18} color="var(--mantine-color-gray-6)" />
          <Text fw={600}>No documentation found</Text>
        </Group>
      );

    default:
      return null;
  }
};

/**
 * Props for the content zone sub-component.
 * @agentic connections
 */
type ContentZoneProps = {
  state: DerivedState;
  availableCompositions: CallCompositionOption[];
  selectedCompositionId: string | null;
  appliedComposition: CallCompositionOption | null | undefined;
  isPolling: boolean;
  onCompositionSelect: (compositionId: string) => void;
};

/**
 * Content zone for scribe prefill status.
 * @agentic connections
 */
const ContentZone = ({
  state,
  availableCompositions,
  selectedCompositionId,
  appliedComposition,
  isPolling,
  onCompositionSelect,
}: ContentZoneProps) => {
  switch (state) {
    case "generating":
      return (
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            The transcription, summarization, and prefill process may take 3–4
            minutes following the completion of the call.
          </Text>
          {availableCompositions.length > 0 && (
            <CallCompositionSelect
              options={availableCompositions}
              value={selectedCompositionId}
              onChange={onCompositionSelect}
              isPolling={isPolling}
            />
          )}
        </Stack>
      );

    case "selecting":
      return (
        <CallCompositionSelect
          options={availableCompositions}
          value={selectedCompositionId}
          onChange={onCompositionSelect}
          isPolling={isPolling}
        />
      );

    case "applied":
      if (appliedComposition) {
        return (
          <Stack gap="xs">
            <AppliedCallCard composition={appliedComposition} />
            {appliedComposition.dialpadCallId && (
              <TranscriptViewer
                dialpadCallId={appliedComposition.dialpadCallId}
              />
            )}
            <Text size="sm" c="dimmed">
              You can edit any field below.
            </Text>
          </Stack>
        );
      }
      return (
        <Text size="sm" c="dimmed">
          You can edit any field below.
        </Text>
      );

    case "error":
      return (
        <Text size="sm" c="dimmed">
          We couldn't find generated documentation. You can enter notes manually
          or try again.
        </Text>
      );

    default:
      return null;
  }
};

/**
 * Props for the applied call card sub-component.
 * @agentic connections
 */
type AppliedCallCardProps = {
  composition: CallCompositionOption;
};

/**
 * Displays the applied call composition in the scribe status UI.
 * @agentic connections
 */
const AppliedCallCard = ({ composition }: AppliedCallCardProps) => {
  const label = [formatCallDate(composition.date)];
  if (composition.durationMs !== undefined) {
    label.push(formatCallDuration(composition.durationMs));
  }

  return (
    <Paper p="sm" radius="md" withBorder className={styles.appliedCard}>
      <Group gap="sm" wrap="nowrap">
        <IconPhone size={18} className={styles.appliedCardIcon} />
        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Text size="sm" fw={500}>
            {label.join(" • ")}
          </Text>
          {composition.authorDisplay && (
            <Text size="xs" c="dimmed">
              {composition.authorDisplay}
            </Text>
          )}
        </Stack>
        <IconCheck size={18} className={styles.appliedCardIcon} />
      </Group>
    </Paper>
  );
};

/**
 * Props for the action zone sub-component.
 * @agentic connections
 */
type ActionZoneProps = {
  state: DerivedState;
  onManualOverride: () => void;
  onRetryPrefill: () => void;
  onChangeCall: () => void;
};

/**
 * Action zone with manual override and retry buttons.
 * @agentic connections
 */
const ActionZone = ({
  state,
  onManualOverride,
  onRetryPrefill,
  onChangeCall,
}: ActionZoneProps) => {
  switch (state) {
    case "generating":
      return (
        <Group gap="xs">
          <Button size="xs" variant="default" onClick={onManualOverride}>
            Enter manually
          </Button>
        </Group>
      );

    case "selecting":
      return (
        <Group gap="xs">
          <Button size="xs" variant="default" onClick={onManualOverride}>
            Enter manually instead
          </Button>
          <Button
            size="xs"
            variant="light"
            color="purple"
            onClick={onRetryPrefill}
          >
            Refresh calls
          </Button>
        </Group>
      );

    case "applied":
      return (
        <Group gap="xs">
          <Button size="xs" variant="subtle" onClick={onChangeCall}>
            Change call
          </Button>
          <Button
            size="xs"
            variant="light"
            color="purple"
            onClick={onRetryPrefill}
          >
            Refresh calls
          </Button>
        </Group>
      );

    case "error":
      return (
        <Group gap="xs">
          <Button size="xs" variant="default" onClick={onManualOverride}>
            Enter manually
          </Button>
          <Button
            size="xs"
            variant="light"
            color="purple"
            onClick={onRetryPrefill}
          >
            Retry prefill
          </Button>
        </Group>
      );

    default:
      return null;
  }
};
